import { Controller } from "@hotwired/stimulus";

const INPUT_ERROR_CLASS = "tw:!text-[#c92d4c]";
const STOCK_ERROR_CLASS = "tw:text-[#c92d4c]";
const UNAVAILABLE_CLASS = "--unavailable";

export default class extends Controller {
  static targets = [
    "quantity",
    "decrement",
    "increment",
    "submit",
    "stock",
    "stockCount",
    "stockOptionsSuffix",
    "card",
    "accessory",
    "accessoryQuantity"
  ];
  static values = {
    stock: Number,
    capacities: Object,
    labels: Object
  };

  connect() {
    this.recompute();
  }

  recompute() {
    const requested = this.#requestedQuantity();
    const available = this.#availableFor(this.#pickedQuantities());

    this.#renderQuantity(requested, available);
    this.#renderStock(requested, available);
    this.#renderCards(requested);
    this.#renderCaps(requested);
  }

  #pickedQuantities() {
    return this.accessoryTargets
      .filter((input) => input.checked)
      .reduce((picked, input) => {
        const { accessoryId } = input.dataset;
        picked[accessoryId] = Math.max(picked[accessoryId] || 1, this.#quantityFor(accessoryId));
        return picked;
      }, {});
  }

  #quantityFor(accessoryId) {
    const input = this.accessoryQuantityTargets.find((target) => target.dataset.accessoryId === accessoryId);
    return Math.max(Number(input?.value) || 1, 1);
  }

  #availableFor(pickedQuantities) {
    return Object.entries(pickedQuantities).reduce(
      (available, [accessoryId, picked]) => Math.min(available, this.#capacityFor(accessoryId, picked)),
      this.stockValue
    );
  }

  #capacityFor(accessoryId, picked) {
    const capacity = this.capacitiesValue[accessoryId];
    return capacity === undefined ? Infinity : Math.floor(capacity / picked);
  }

  #requestedQuantity() {
    return Math.max(Number(this.quantityTarget.value) || 1, 1);
  }

  #renderQuantity(requested, available) {
    const boundMin = Math.min(1, available);
    const boundMax = Math.max(boundMin, available, 1);
    const over = requested > boundMax;

    this.quantityTarget.min = boundMin;
    this.quantityTarget.max = boundMax;
    this.quantityTarget.classList.toggle(INPUT_ERROR_CLASS, over);

    Object.entries({
      decrement: requested <= boundMin,
      increment: requested >= boundMax,
      submit: over
    }).forEach(([name, disabled]) => {
      this[`${name}Targets`].forEach((button) => {
        button.disabled = disabled;
      });
    });
  }

  #renderStock(requested, available) {
    this.stockCountTarget.textContent = this.#countLabel(available);
    this.stockTarget.classList.toggle(STOCK_ERROR_CLASS, requested > available);
    this.stockOptionsSuffixTargets.forEach((suffix) => {
      suffix.hidden = available >= this.stockValue;
    });
  }

  #countLabel(count) {
    if (count === 0) return this.labelsValue.zero;
    if (count === 1) return this.labelsValue.one;
    return this.labelsValue.other.replace("%{count}", count);
  }

  #renderCards(requested) {
    this.cardTargets.forEach((card) => {
      const input = this.#accessoryInput(card.dataset.accessoryId);
      const unavailable = !input?.checked && this.#capacityFor(card.dataset.accessoryId, 1) < requested;

      card.classList.toggle(UNAVAILABLE_CLASS, unavailable);
      if (input) input.disabled = unavailable;
    });
  }

  #renderCaps(requested) {
    this.accessoryQuantityTargets.forEach((input) => {
      const capacity = this.#capacityFor(input.dataset.accessoryId, requested);

      if (Number.isFinite(capacity)) input.dataset.maxQuantity = Math.max(capacity, 1);
      else delete input.dataset.maxQuantity;
    });

    this.dispatch("caps", { target: window });
  }

  #accessoryInput(accessoryId) {
    return this.accessoryTargets.find((target) => target.dataset.accessoryId === accessoryId);
  }
}
